/* ============================================================================
   FACTURACIÓN — la pantalla

   Dos cosas que no se mezclan:

     · el INFORME de horas por cliente, con la tarifa que regía en cada vuelo;
     · la DECLARACIÓN de tarifas, que es un dato del operador y queda con
       fuente, fecha y quién la cargó.

   Sin tarifa declarada no hay importe. La pantalla muestra las horas aparte en
   vez de multiplicarlas por cero: un cero se lee como «gratis» y no lo es.

   La tarifa es comercial: la ve quien tiene `facturacion.leer` (administración
   y auditoría) y la declara quien tiene `facturacion.escribir`. El piloto y
   mantenimiento no la ven, y el administrador de plataforma tampoco: su alcance
   no incluye los datos de operación de la empresa.
   ========================================================================== */

import { useMemo, useState } from "react";

import { facturar, tarifaVigente } from "../../domain/estadisticas.js";
import { puede } from "../../domain/identidad.js";
import "../administracion/administracion.css";
import "../estadisticas/estadisticas.css";

const hoy = () => new Date().toISOString().slice(0, 10);
const primerDiaDelAnio = `${new Date().getFullYear()}-01-01`;

function nombreDeCliente(clientes, id) {
  const c = (Array.isArray(clientes) ? clientes : []).find((x) => x && x.id === id);
  return c?.nombre || id || "(sin cliente)";
}

function importe(valor, moneda) {
  if (valor === null || valor === undefined) return "—";
  try {
    return new Intl.NumberFormat("es-EC", {
      style: "currency",
      currency: moneda,
      maximumFractionDigits: 2,
    }).format(valor);
  } catch {
    return `${moneda || ""} ${Number(valor).toFixed(2)}`.trim();
  }
}

export default function PanelFacturacion({
  bitacora = [],
  clientes = [],
  tarifas = [],
  alCambiarTarifas = null,
  perfil = null,
  usuario = null,
}) {
  const puedeDeclarar = puede(perfil, "facturacion.escribir");

  const [desde, setDesde] = useState(primerDiaDelAnio);
  const [hasta, setHasta] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [valorHora, setValorHora] = useState("");
  const [moneda, setMoneda] = useState("USD");
  const [vigenteDesde, setVigenteDesde] = useState(hoy);
  const [fuente, setFuente] = useState("");
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");

  const listaTarifas = useMemo(
    () => (Array.isArray(tarifas) ? tarifas.filter(Boolean) : []),
    [tarifas],
  );

  const informe = useMemo(
    () => facturar(bitacora, {
      clientes,
      tarifas: listaTarifas,
      desde: desde || null,
      hasta: hasta || null,
      empresaId: usuario?.empresaId || null,
    }),
    [bitacora, clientes, listaTarifas, desde, hasta, usuario?.empresaId],
  );

  const declarar = (evento) => {
    evento.preventDefault();
    setError("");
    setAviso("");

    if (!puedeDeclarar) {
      setError("Tu perfil puede consultar la facturación, pero no declarar tarifas.");
      return;
    }
    if (!clienteId) { setError("Elige el cliente."); return; }
    if (!vigenteDesde) { setError("Indica desde qué fecha rige la tarifa."); return; }
    if (valorHora === "" || !Number.isFinite(Number(valorHora)) || Number(valorHora) < 0) {
      setError("La tarifa por hora tiene que ser un número mayor o igual a cero.");
      return;
    }
    const codigo = moneda.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(codigo)) {
      setError("La moneda son tres letras, por ejemplo USD o EUR.");
      return;
    }

    const repetida = listaTarifas.some(
      (t) => t.clienteId === clienteId && t.desde === vigenteDesde && t.moneda === codigo,
    );
    if (repetida) {
      setError("Ese cliente ya tiene una tarifa en esa moneda desde esa fecha.");
      return;
    }

    const nueva = {
      clienteId,
      valorHora: Number(valorHora),
      moneda: codigo,
      desde: vigenteDesde,
      fuente: fuente.trim() || null,
      quien: usuario?.nombre || usuario?.id || null,
      el: new Date().toISOString(),
    };

    alCambiarTarifas?.([...listaTarifas, nueva].sort((a, b) => {
      const porCliente = String(a.clienteId).localeCompare(String(b.clienteId));
      return porCliente !== 0 ? porCliente : String(a.desde).localeCompare(String(b.desde));
    }));

    setAviso(`Tarifa de ${nombreDeCliente(clientes, clienteId)} declarada desde el ${vigenteDesde}.`);
    setValorHora("");
    setFuente("");
  };

  const historial = useMemo(
    () => [...listaTarifas].sort((a, b) => String(b.desde).localeCompare(String(a.desde))),
    [listaTarifas],
  );

  const sinClientes = !Array.isArray(clientes) || clientes.length === 0;
  const totalHoras = informe.filas.reduce((s, f) => s + f.horas, 0);
  const totalCubiertas = informe.filas.reduce((s, f) => s + f.cubiertas, 0);
  const sinTarifaEnTabla = informe.filas.reduce((s, f) => s + f.sinTarifa, 0);

  return (
    <section className="adm">
      <header className="adm__entrada">
        <div className="adm__entrada-texto">
          <h2 className="adm__titulo">Facturación</h2>
          <p className="adm__nota">
            Las mismas horas del informe, por cliente y por la tarifa que regía en la fecha de
            cada vuelo. Una tarifa que cambió a mitad de mes no se aplica hacia atrás.
          </p>
        </div>
      </header>

      {error && <p className="adm__mensaje adm__mensaje--problema" role="alert">{error}</p>}
      {aviso && <p className="adm__mensaje adm__mensaje--ok" role="status">{aviso}</p>}

      <div className="adm__seccion">
        <div className="est__controles hc-no-print">
          <label className="est__campo">
            <span>Desde</span>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </label>
          <label className="est__campo">
            <span>Hasta</span>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </label>
          {(desde || hasta) && (
            <button
              type="button"
              className="est__limpiar"
              onClick={() => { setDesde(""); setHasta(""); }}
            >
              Todo el libro
            </button>
          )}
        </div>

        {informe.filas.length === 0 ? (
          <p className="adm__nota">
            No hay vuelos con cliente en ese período.
            {informe.sinCliente.vuelos > 0
              ? ` Hay ${informe.sinCliente.vuelos} vuelo(s) sin cliente que no se pueden facturar a nadie.`
              : ""}
          </p>
        ) : (
          <div className="adm__tabla-caja">
            <table className="adm__tabla">
              <caption className="adm__caption">
                Horas por cliente · {desde || "inicio"} a {hasta || "hoy"}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Cliente</th>
                  <th scope="col">Horas</th>
                  <th scope="col">Con tarifa</th>
                  <th scope="col">Sin tarifa</th>
                  <th scope="col">Importe</th>
                </tr>
              </thead>
              <tbody>
                {informe.filas.map((f) => (
                  <tr key={f.clienteId}>
                    <th scope="row">{f.nombre}</th>
                    <td className="est__num">{f.horas.toFixed(1)}</td>
                    <td className="est__num">{f.cubiertas.toFixed(1)}</td>
                    <td className="est__num">{f.sinTarifa.toFixed(1)}</td>
                    <td className="est__num">
                      {f.importe !== null
                        ? importe(f.importe, f.moneda)
                        : f.porMoneda.length > 0
                          ? f.porMoneda.map((m) => importe(m.importe, m.moneda)).join(" + ")
                          : <span className="adm__alerta">Sin tarifa declarada</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  <td className="est__num">{totalHoras.toFixed(1)}</td>
                  <td className="est__num">{totalCubiertas.toFixed(1)}</td>
                  <td className="est__num">{sinTarifaEnTabla.toFixed(1)}</td>
                  <td className="est__num">
                    {informe.totales.length === 0
                      ? "—"
                      : informe.totales.map((t) => importe(t.importe, t.moneda)).join(" + ")}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {(informe.horasSinTarifa > 0 || informe.sinCliente.vuelos > 0) && (
          <p className="adm__nota adm__alerta">
            {informe.horasSinTarifa.toFixed(1)} h no tienen tarifa declarada
            {informe.sinCliente.vuelos > 0
              ? ` (${informe.sinCliente.vuelos} vuelo(s) sin cliente)`
              : ""}. No se les pone un importe: se cobran cuando el operador declare la tarifa.
          </p>
        )}

        <p className="adm__resumen">
          Asientos contados: <strong>{informe.procedencia.asientos}</strong> ·
          fuera por discrepancia con el papel: <strong>{informe.procedencia.excluidosPorDiscrepancia}</strong> ·
          sin fecha: <strong>{informe.procedencia.sinFecha}</strong>
        </p>
      </div>

      {puedeDeclarar ? (
        <form className="adm__alta" onSubmit={declarar}>
          <h3 className="adm__alta-titulo">Declarar una tarifa</h3>

          <label className="adm__campo">
            <span>Cliente</span>
            <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} disabled={sinClientes}>
              <option value="">{sinClientes ? "No hay clientes cargados" : "Elige uno"}</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre || c.id}</option>
              ))}
            </select>
          </label>

          <label className="adm__campo">
            <span>Valor por hora</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={valorHora}
              onChange={(e) => setValorHora(e.target.value)}
              placeholder="0.00"
            />
          </label>

          <label className="adm__campo">
            <span>Moneda</span>
            <input
              type="text"
              value={moneda}
              maxLength={3}
              onChange={(e) => setMoneda(e.target.value.toUpperCase())}
              placeholder="USD"
            />
          </label>

          <label className="adm__campo">
            <span>Rige desde</span>
            <input type="date" value={vigenteDesde} onChange={(e) => setVigenteDesde(e.target.value)} />
          </label>

          <label className="adm__campo">
            <span>Fuente (contrato, adenda, correo)</span>
            <input
              type="text"
              value={fuente}
              onChange={(e) => setFuente(e.target.value)}
              placeholder="Contrato 2026"
            />
          </label>

          <button type="submit" className="adm__boton">Declarar tarifa</button>

          {sinClientes && (
            <p className="adm__nota">
              Para facturar hace falta un cliente. Se carga en «Empresa y usuarios».
            </p>
          )}

          {clienteId && (
            <p className="adm__nota">
              Tarifa vigente hoy para {nombreDeCliente(clientes, clienteId)}:{" "}
              {(() => {
                const t = tarifaVigente(listaTarifas, clienteId, hoy());
                return t ? `${importe(t.valorHora, t.moneda)} por hora desde ${t.desde}` : "sin tarifa declarada";
              })()}
            </p>
          )}
        </form>
      ) : (
        <p className="adm__bloqueo" role="status">
          Solo lectura: tu perfil puede consultar la facturación, pero no declarar tarifas.
        </p>
      )}

      <div className="adm__seccion">
        <h3 className="adm__subtitulo">Tarifas declaradas</h3>
        {historial.length === 0 ? (
          <p className="adm__nota">Todavía no hay tarifas declaradas.</p>
        ) : (
          <div className="adm__tabla-caja">
            <table className="adm__tabla">
              <caption className="adm__caption">Cada tarifa conserva su fecha, su fuente y quién la cargó.</caption>
              <thead>
                <tr>
                  <th scope="col">Desde</th>
                  <th scope="col">Cliente</th>
                  <th scope="col">Valor por hora</th>
                  <th scope="col">Fuente</th>
                  <th scope="col">Quién</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((t, i) => (
                  <tr key={`${t.clienteId}-${t.desde}-${t.moneda}-${i}`}>
                    <th scope="row">{t.desde}</th>
                    <td>{nombreDeCliente(clientes, t.clienteId)}</td>
                    <td>{importe(Number(t.valorHora), t.moneda)}</td>
                    <td>{t.fuente || "—"}</td>
                    <td>{t.quien || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
